// One pallet on the inspection page — today's card (number, EXPORT/LOCAL,
// variety · grade · size, grid, the age clock and bar, tap to expand,
// Approve / Reject), plus (F03):
//   UF-04 the pallet number opens the pallet, and the card names its run (a
//         link where the viewer may open runs) and grower; both links stamp
//         this page as the way Back.
//   UF-02 Approve and Reject confirm ON the card — an inline tick, not a
//         full-screen card covering the queue for a second or more, ~60 times
//         a day. The page's useHeldVerdicts keeps the card in place after the
//         verdict so the tick is seen: Approved for a moment; Rejected until
//         dismissed, naming the pallet and its run, with Open pallet.
//   UF-05 a run with a QC FAIL nobody has released says so on the card —
//         "QC FAIL on run R-1039 · Open check" — and Approve reads "Blocked
//         by QC" before anyone taps it (Reject still works). The database
//         trigger stays the boundary: a refusal the card could not foresee
//         is one choice, open the failed check or close (useExplainQcRefusal).
// Approve still asks nothing first; Reject still opens the Record Rejection
// sheet with the official PPECB reasons.
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { statusToneClasses } from '@/lib/statusTone'
import StatusPill from '@/components/ui/StatusPill'
import { CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react'
import SubmitOverlay from '@/components/SubmitOverlay'
import { errorHandledElsewhere, type OverlayFlags } from '@/components/MutationOverlays'
import { useReturnHereState } from '@/lib/returnTo'
import { qcCheckHref } from '@/lib/qcCheckLink'
import { ageHours, ageLimitHours, fmtAge, rejectedLine, type QueuePallet } from './inspectionQueue'
import InspectionAgeChip from '@/components/InspectionAgeChip'
import { inspectionAge, STALE_ADVICE } from '@/lib/inspectionAge'
import type { InspectionDate, QcHold, RunName, TruckPlan } from './inspectionReads'
import type { Verdict } from './useHeldVerdicts'
import { useApprovePallet } from './useApprovePallet'
import { useRejectPallet } from './useRejectPallet'
import { useExplainQcRefusal } from './useExplainQcRefusal'
import PalletLink from './PalletLink'
import { ppecbNoteText } from './ppecbNote'

/** What the card hands the page's Record Rejection sheet (Inspection.tsx
 *  RejectModal — today's, unchanged): the card keeps the write, so its tick
 *  lands on the card. */
export interface RejectSheetSlot {
  isPending: boolean
  onConfirm: (code: string, reason: string, notes: string) => void
  onCancel:  () => void
}

/** Approve's tick flags: pending and the tick only — a refusal is today's
 *  "Approval blocked" dialog (useApprovePallet), not a red line as well. */
function tickFlags(m: OverlayFlags) {
  const f = errorHandledElsewhere(m)
  return { isPending: f.isPending, isSuccess: f.isSuccess, isError: f.isError, error: f.error }
}

/** UX-laws U19 UF-06: the age clock and bar wear the house tones (70% / 90%
 *  thresholds unchanged); the card's border no longer changes colour. */
function ageTone(pct: number): { text: string; pill: string; bar: string } {
  const c = statusToneClasses(pct >= 90 ? 'bad' : pct >= 70 ? 'attention' : 'good')
  return { text: c.text, pill: c.pill, bar: c.dot }
}

/** "Run R-1039 · Grower" — the run a link only where the viewer may open runs. */
function RunLine({ runId, run, canOpenRun }: { runId: string | null; run?: RunName; canOpenRun: boolean }) {
  const here = useReturnHereState()
  if (!runId || !run) return null
  return (
    <p className="text-xs text-muted mt-0.5 truncate">
      Run{' '}
      {canOpenRun
        ? <Link to={`/runs/${runId}`} state={here} title={`Open run ${run.run_number}`}
            className="pointer-events-auto inline-flex items-center min-h-[44px] -my-3.5 font-mono text-blue-300 hover:underline underline-offset-2">
            {run.run_number}
          </Link>
        : <span className="font-mono text-fg-secondary">{run.run_number}</span>}
      {run.grower ? <> · {run.grower}</> : null}
    </p>
  )
}

function Details({ pallet, hours, isExport, tone, approved }: { pallet: QueuePallet; hours: number; isExport: boolean; tone: string; approved: boolean }) {
  return (
    <div className="px-4 pt-3 pb-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm border-t hairline-strong mt-2">
      <div className="text-fg-muted">Net Weight</div>
      <div className="text-fg">{pallet.net_weight_kg ? `${pallet.net_weight_kg} kg` : '—'}</div>
      <div className="text-fg-muted">Packed</div>
      <div className="text-fg">
        {pallet.packed_at ? new Date(pallet.packed_at).toLocaleDateString('en-ZA') : '—'}
      </div>
      <div className="text-fg-muted">Stocked At</div>
      <div className="text-fg">
        {pallet.stocked_at
          ? new Date(pallet.stocked_at).toLocaleString('en-ZA', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })
          : '—'}
      </div>
      {!approved && <>
        <div className="text-fg-muted">Age Limit</div>
        <div className={clsx('font-medium', tone)}>{fmtAge(hours)} / {isExport ? '48h' : '30d'}</div>
      </>}
    </div>
  )
}

/** A rejected pallet's line: what happened, and where it goes next. */
function RejectedFoot({ pallet, run, canOpenPallet, canOpenCompliance, onDismiss }: {
  pallet: QueuePallet; run?: RunName; canOpenPallet: boolean; canOpenCompliance?: boolean; onDismiss?: () => void
}) {
  const here = useReturnHereState()
  return (
    <div className="rounded-xl border border-red-700 bg-red-900/30 px-3 py-2 space-y-2">
      <p className="text-sm text-red-200 flex items-start gap-2">
        <XCircle size={16} className="shrink-0 mt-0.5" aria-hidden />
        <span className="min-w-0 break-words">{rejectedLine(pallet.pallet_id, run?.run_number)}</span>
      </p>
      {/* UF-10: both reject triggers (HOUSE_REJECT, PPECB_REJECT) open a
          non-conformance. Say so, and link it only where Compliance opens. */}
      <p className="text-sm text-red-200" data-testid="nc-opened-note">
        A non-conformance was opened for this reject.{' '}
        {canOpenCompliance ? '' : 'A manager closes it on Compliance.'}
      </p>
      {(canOpenPallet || canOpenCompliance || onDismiss) && (
        <div className="flex gap-2 flex-wrap">
          {canOpenCompliance && (
            <Link to="/compliance?tab=ncs" state={here} className="btn-secondary btn-sm">Open non-conformances</Link>
          )}
          {canOpenPallet && (
            <Link to={`/pallets/${pallet.pallet_id}`} state={here} className="btn-secondary btn-sm">Open pallet</Link>
          )}
          {onDismiss && <button type="button" onClick={onDismiss} className="btn-ghost btn-sm">Dismiss</button>}
        </div>
      )}
    </div>
  )
}

/** "QC FAIL on run R-1039 · Open check" — the check a link where the viewer
 *  may open QC. */
function QcHoldLine({ hold, held, run, canOpenQc, canRelease }: { hold?: QcHold; held?: Verdict; run?: RunName; canOpenQc?: boolean; canRelease?: boolean }) {
  const here = useReturnHereState()
  // A rejected card names its outcome instead: the hold no longer matters.
  if (!hold || held === 'REJECTED') return null
  return (
    <div role="status"
      className="mb-3 flex items-center gap-x-2 flex-wrap rounded-xl border border-red-700 bg-red-900/30 px-3 text-sm text-red-200">
      <ShieldAlert size={16} className="shrink-0" aria-hidden />
      <span className="py-2.5 min-w-0 break-words">
        {run?.run_number
          ? <>QC FAIL on run <span className="font-mono">{run.run_number}</span></>
          : <>QC FAIL on this pallet&apos;s run</>}
      </span>
      {/* UF-02: say who can release it. A holder of qc manage opens the check
          to release it; anyone else is told it is a manager's call. */}
      {!canRelease && (
        <>
          <span aria-hidden className="text-red-400 whitespace-pre"> · </span>
          <span className="py-2.5">Only a QC manager can release it</span>
        </>
      )}
      {canOpenQc && (
        <>
          <span aria-hidden className="text-red-400 whitespace-pre"> · </span>
          <Link to={qcCheckHref(hold)} state={here}
            className="inline-flex items-center min-h-[44px] font-semibold text-red-100 underline underline-offset-2">
            {canRelease ? 'Open check to release it' : 'Open check'}
          </Link>
        </>
      )}
    </div>
  )
}

/** UF-13: is a truck planned for this approved pallet? Nothing while unread. */
function truckText(plan: TruckPlan | undefined): string | null {
  if (!plan) return null
  if (plan.consignment) return `On ${plan.consignment}`
  return plan.mission ? 'On a loading mission' : 'Not on a truck'
}

function ApprovedBand({ truck, inspection, isExport }: { truck?: TruckPlan; inspection?: InspectionDate; isExport: boolean }) {
  const line = truckText(truck)
  return (
    <div className="py-1 text-center">
      <StatusPill object="pallet" status="INSPECTED_APPROVED" icon={<CheckCircle size={14} aria-hidden />} />
      {line && <p data-testid="approved-truck" className="text-caption text-fg-secondary mt-1">{line}</p>}
      {/* UF-15: the PPECB 21 d soft / 28 d hard re-inspect limit, from the approval's own date. Export pallets only, as in Loading Bay. */}
      {inspection && <InspectionAgeChip inspectedAt={inspection.inspected_at} commodity={inspection.commodity} show={isExport} className="mt-1 inline-block" />}
      {inspection && isExport && inspectionAge(inspection.inspected_at, inspection.commodity).kind === 'stale' && (
        <p data-testid="approved-stale-line" className="text-caption text-fg-secondary mt-1">{STALE_ADVICE}</p>
      )}
    </div>
  )
}

/** Approve (asks nothing) and Reject (opens the Record Rejection sheet). */
function VerdictButtons({ busy, approving, qcBlocked, onApprove, onReject }: {
  busy: boolean; approving: boolean; qcBlocked: boolean; onApprove: () => void; onReject: () => void
}) {
  return (
    <div className="flex gap-2">
      {qcBlocked
        ? (
          <button type="button" disabled
            className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 min-h-[44px]
                       surface-raised border hairline-strong text-gray-400 font-semibold text-sm">
            <ShieldAlert size={16} aria-hidden />
            Blocked by QC
          </button>
        )
        : (
          <button type="button" disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 min-h-[44px]
                       bg-green-600 text-white font-semibold text-sm
                       active:scale-95 transition-[transform,background-color,opacity] disabled:opacity-60"
            onClick={onApprove}>
            <CheckCircle size={16} aria-hidden />
            {approving ? 'Approving…' : 'Approve'}
          </button>
        )}
      <button type="button" disabled={busy}
        className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 min-h-[44px]
                   bg-red-900/60 border border-red-700 text-red-300 font-semibold text-sm
                   active:scale-95 transition-[transform,background-color,opacity] disabled:opacity-60"
        onClick={onReject}>
        <XCircle size={16} aria-hidden />
        Reject
      </button>
    </div>
  )
}

/** EXPORT / LOCAL. */
function ChannelChip({ isExport }: { isExport: boolean }) {
  return (
    <span className={clsx('shrink-0 text-xs rounded-full px-2 py-0.5 font-semibold',
      'border border-line-strong text-fg-secondary')}>
      {isExport ? 'EXPORT' : 'LOCAL'}
    </span>
  )
}

/** Variety · grade · size. */
function FruitLine({ pallet }: { pallet: QueuePallet }) {
  return (
    <p className="text-sm text-fg-secondary">
      {pallet.runs?.variety ?? '—'}
      {pallet.grade && <span className="text-muted"> · {pallet.grade}</span>}
      {pallet.size_code && <span className="text-muted"> · {pallet.size_code}</span>}
    </p>
  )
}

/** The cold-room grid, when the pallet has one. */
function GridLine({ grid }: { grid: string | null }) {
  if (!grid) return null
  return <p className="text-xs text-teal-400 mt-0.5">Grid {grid}</p>
}

/** The card's header expands it: this button is stretched under the header,
 *  and the links sit above it. */
function ExpandButton({ expanded, id, onToggle }: { expanded: boolean; id: string; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={expanded}
      aria-label={`${expanded ? 'Hide' : 'Show'} details of pallet ${id}`}
      className="absolute inset-0 w-full h-full" />
  )
}

/** The age clock and the expand chevron, top right. */
function AgeClock({ hours, tone, expanded, approved }: { hours: number; tone: string; expanded: boolean; approved: boolean }) {
  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      {/* UF-15: the 48 h clock counts a limit an Approved pallet has passed. */}
      {!approved && (
        <span className={clsx('inline-flex items-center rounded-full px-2 py-[3px] text-sm font-bold', tone)}>
          <Clock size={12} className="inline mr-0.5" aria-hidden />{fmtAge(hours)}
        </span>
      )}
      {expanded
        ? <ChevronUp size={16} className="text-muted" aria-hidden />
        : <ChevronDown size={16} className="text-muted" aria-hidden />}
    </div>
  )
}

/** UF-20: a pallet on an undecided PPECB consignment has no hand verdict. A
 *  hand Approve would let it escape a later PPECB FAIL, and would stop the
 *  consignment being recalled. Recall the consignment first. The wording
 *  (ppecbNoteText) follows the consignment and who can act on it. */
function WithPpecbNote({ consignmentStatus, onTitan, canAmend, canRecall }: { consignmentStatus?: string; onTitan?: boolean; canAmend?: boolean; canRecall?: boolean }) {
  return (
    <p className="text-sm text-blue-300 surface-sunk rounded-lg px-3 py-2" data-testid="with-ppecb-note">
      {ppecbNoteText(consignmentStatus, { onTitan, canAmend, canRecall })}
    </p>
  )
}

/** Under the card: the verdict once there is one, else the buttons. */
function CardFoot({ held, pallet, run, canOpenPallet, canOpenCompliance, onDismissRejected, buttons, consignmentStatus, onTitan, canAmend, canRecall, truck, inspection }: {
  truck?: TruckPlan; inspection?: InspectionDate; held?: Verdict; pallet: QueuePallet; run?: RunName; canOpenPallet: boolean; canOpenCompliance?: boolean; consignmentStatus?: string; onTitan?: boolean; canAmend?: boolean; canRecall?: boolean
  onDismissRejected?: () => void; buttons: ReactNode
}) {
  if (held === 'REJECTED') {
    return <RejectedFoot pallet={pallet} run={run} canOpenPallet={canOpenPallet} canOpenCompliance={canOpenCompliance} onDismiss={onDismissRejected} />
  }
  if (held === 'APPROVED' || pallet.status === 'INSPECTED_APPROVED') return <ApprovedBand truck={truck} inspection={inspection} isExport={pallet.channel === 'EXPORT'} />
  if (pallet.ppecb_consignment_id) return <WithPpecbNote consignmentStatus={consignmentStatus} onTitan={onTitan} canAmend={canAmend} canRecall={canRecall} />
  return <>{buttons}</>
}

/** Approve, with a QC-gate refusal explained as one choice (UF-05). */
function useCardApprove(qcHold: QcHold | undefined, canOpenQc: boolean | undefined,
  onVerdict: (pallet: QueuePallet, outcome: Verdict) => void) {
  const explainQcRefusal = useExplainQcRefusal(qcHold, canOpenQc)
  return useApprovePallet<QueuePallet>(p => onVerdict(p, 'APPROVED'), (p, e) => { void explainQcRefusal(p, e) })
}

/** The card's top: number, channel, fruit, run, grid and the age clock. The
 *  whole header still expands the card: the expand button is stretched under
 *  it, and the links sit above it. */
function CardHeader({ pallet, run, canOpenPallet, canOpenRun, isExport, hours, tone, expanded, setExpanded, approved }: {
  pallet: QueuePallet; run?: RunName; canOpenPallet: boolean; canOpenRun: boolean
  isExport: boolean; hours: number; tone: string; expanded: boolean; setExpanded: (f: (e: boolean) => boolean) => void
  approved: boolean
}) {
  const id = pallet.pallet_id
  return (
    <div className="relative">
      <ExpandButton expanded={expanded} id={id} onToggle={() => setExpanded(e => !e)} />
      <div className="relative pointer-events-none p-4 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 min-w-0">
            <PalletLink id={id} canOpen={canOpenPallet}
              className={clsx('font-mono font-bold text-fg text-base', canOpenPallet && 'pointer-events-auto')} />
            <ChannelChip isExport={isExport} />
          </div>
          <FruitLine pallet={pallet} />
          <RunLine runId={pallet.run_id} run={run} canOpenRun={canOpenRun} />
          <GridLine grid={pallet.coldroom_grid} />
        </div>
        <AgeClock hours={hours} tone={tone} expanded={expanded} approved={approved} />
      </div>
    </div>
  )
}

/** The ticks, mounted whatever the foot shows, so they survive the card
 *  flipping from its buttons to its verdict. Approve's leaves on a refusal
 *  (the dialog says it), so its spinner can't outlive the write. Reject's
 *  refusal is its red line, under the card. */
function VerdictTicks({ id, approve, reject }: {
  id: string
  approve: ReturnType<typeof useCardApprove>
  reject: ReturnType<typeof useRejectPallet<QueuePallet>>
}) {
  return (
    <>
      {!approve.isError && (
        <SubmitOverlay inline {...tickFlags(approve)}
          pendingLabel={`Approving pallet ${id}…`} successLabel={`Pallet ${id} approved`}
          onDismiss={() => approve.reset()} />
      )}
      <SubmitOverlay inline isPending={reject.isPending} isSuccess={reject.isSuccess}
        isError={reject.isError} error={reject.error}
        pendingLabel={`Rejecting pallet ${id}…`} successLabel={`Pallet ${id} rejected`}
        onDismiss={() => reject.reset()} />
    </>
  )
}

/** Mounts the page's Record Rejection sheet over the card's own reject write. */
function RejectSheetHost({ pallet, reject, rejectSheet, setRejecting }: {
  pallet: QueuePallet
  reject: ReturnType<typeof useRejectPallet<QueuePallet>>
  rejectSheet: (s: RejectSheetSlot) => ReactNode
  setRejecting: (v: boolean) => void
}) {
  const onClose = () => setRejecting(false)
  return <>{rejectSheet({
    isPending: reject.isPending,
    onCancel:  onClose,
    // Closes once the write settles: a refusal is the red line on the
    // card, which the sheet would cover.
    onConfirm: (code, reason, notes) =>
      reject.mutate({ pallet, rejection: { code, reason, notes } }, { onSettled: onClose }),
  })}</>
}

/** CardFoot with the Approve / Reject buttons wired to the card's own writes. */
function CardVerdictFoot({ approve, busy, qcBlocked, setRejecting, ...foot }: {
  truck?: TruckPlan; inspection?: InspectionDate; held?: Verdict; pallet: QueuePallet; run?: RunName; canOpenPallet: boolean; canOpenCompliance?: boolean
  consignmentStatus?: string; onTitan?: boolean; canAmend?: boolean; canRecall?: boolean; onDismissRejected?: () => void
  approve: ReturnType<typeof useCardApprove>; busy: boolean; qcBlocked: boolean
  setRejecting: (v: boolean) => void
}) {
  return (
    <CardFoot {...foot}
      buttons={<VerdictButtons busy={busy} approving={approve.isPending} qcBlocked={qcBlocked}
        onApprove={() => approve.mutate(foot.pallet)} onReject={() => setRejecting(true)} />} />
  )
}

/** The age numbers a card is drawn from. */
function cardAge(pallet: QueuePallet) {
  const isExport = pallet.channel === 'EXPORT'
  const hours = ageHours(pallet)
  const pct   = Math.min((hours / ageLimitHours(isExport)) * 100, 100)
  return { isExport, hours, pct, tone: ageTone(pct) }
}

function AgingBar({ pct, bar }: { pct: number; bar: string }) {
  return (
    <div className="h-1 surface-raised mx-4">
      <div className={clsx('h-full rounded-full transition-[width]', bar)} style={{ width: `${pct}%` }} />
    </div>
  )
}

export interface QueueCardProps {
  /** The approved pallet's truck (useTruckPlans); absent while unread (UF-13). */
  truck?: TruckPlan
  /** The approved pallet's inspection date and commodity (useInspectionDates); absent while unread (UF-15). */
  inspection?: InspectionDate
  /** Recall (and delete) are manager and above only (DELETE_ROLES on the edge function). */
  canRecall?: boolean
  /** Amend & revalidate / Re-inspection: AMEND_ROLES (adds inspectors and dispatchers). */
  canAmend?: boolean
  /** The consignment has a real TITAN message, so Amend & revalidate can be offered. */
  onTitan?: boolean
  /** inspection_status of the pallet's PPECB consignment, when the page holds it. */
  consignmentStatus?: string
  pallet:         QueuePallet
  /** The pallet's run number + grower (useRunNames); absent while loading. */
  run?:           RunName
  /** useCanOpen('pallets') / useCanOpen('runs') — a link only where it leads. */
  canOpenPallet:  boolean
  canOpenRun:     boolean
  /** An unreleased QC FAIL on the pallet's run (useQcHolds), if any. */
  qcHold?:        QcHold
  /** useCanOpen('qc') — "Open check" only where it leads. */
  canOpenQc?:     boolean
  /** useCanOpen('compliance') — "Open non-conformances" only where it leads (UF-10). */
  canOpenCompliance?: boolean
  /** can('qc','manage') — the server's gate for releasing a FAIL (UF-02). */
  canRelease?:    boolean
  /** The page's Record Rejection sheet, with the official PPECB reasons. */
  rejectSheet:    (s: RejectSheetSlot) => ReactNode
  /** Just approved / rejected here: the card stays in place to say so. */
  held?:          Verdict
  /** A verdict was recorded — the page's useHeldVerdicts `hold`. */
  onVerdict:      (pallet: QueuePallet, outcome: Verdict) => void
  /** Take a rejected card off the list. */
  onDismissRejected?: () => void
}

export default function QueueCard(props: QueueCardProps) {
  const { truck, inspection, pallet, run, canOpenPallet, canOpenRun, qcHold, canOpenQc, canOpenCompliance, canRelease, rejectSheet, held, onVerdict, onDismissRejected, consignmentStatus, onTitan, canAmend, canRecall } = props
  const [expanded, setExpanded]   = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const approve = useCardApprove(qcHold, canOpenQc, onVerdict)
  const reject  = useRejectPallet<QueuePallet>(p => onVerdict(p, 'REJECTED'))
  const { isExport, hours, pct, tone: ageToneFor } = cardAge(pallet)
  const approved = held === 'APPROVED' || pallet.status === 'INSPECTED_APPROVED'
  const tone = ageToneFor
  const id    = pallet.pallet_id
  const busy  = approve.isPending || reject.isPending

  return (
    <>
      <div className="card p-0 overflow-hidden border hairline-strong">
        <CardHeader pallet={pallet} run={run} canOpenPallet={canOpenPallet} canOpenRun={canOpenRun}
          isExport={isExport} hours={hours} tone={tone.pill} expanded={expanded}
          setExpanded={setExpanded} approved={approved} />

        {/* Aging bar */}
        {!approved && <AgingBar pct={pct} bar={tone.bar} />}

        {expanded && <Details pallet={pallet} hours={hours} isExport={isExport} tone={tone.text} approved={approved} />}

        <div className="px-4 pb-4 pt-3">
          <VerdictTicks id={id} approve={approve} reject={reject} />
          <QcHoldLine hold={qcHold} held={held} run={run} canOpenQc={canOpenQc} canRelease={canRelease} />
          <CardVerdictFoot truck={truck} inspection={inspection} held={held} pallet={pallet} run={run} canOpenPallet={canOpenPallet}
            canOpenCompliance={canOpenCompliance} onDismissRejected={onDismissRejected} consignmentStatus={consignmentStatus}
            onTitan={onTitan} canAmend={canAmend} canRecall={canRecall} approve={approve} busy={busy}
            qcBlocked={!!qcHold} setRejecting={setRejecting} />
        </div>
      </div>

      {rejecting && (
        <RejectSheetHost pallet={pallet} reject={reject} rejectSheet={rejectSheet} setRejecting={setRejecting} />
      )}
    </>
  )
}
